import React, { useState, useEffect, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import Button from '@mui/material/Button'; 
import { Box, Typography, Grid, LinearProgress, Paper } from '@mui/material'; 
// ArrowUpwardIcon and ArrowDownwardIcon removed as pagination is gone.
// Document, Page, pdfjs from react-pdf removed.

import ReactQuill, { Quill } from 'react-quill';
import { gql, useQuery } from '@apollo/client';
import 'react-quill/dist/quill.snow.css'; // Standard theme for ReactQuill

// Import the AIAssistant component
import AIAssistant from '../components/AIAssistant';



// --- Custom Quill Blot for Template Variables ---
const Inline = Quill.import('blots/inline');

class TemplateVariableBlot extends Inline {
  static blotName = 'template-variable';
  static tagName = 'span';
  static className = 'template-variable';

  static create(value) {
    // console.log('TemplateVariableBlot.create called. Value:', value); // Logging no longer needed
    const node = super.create(value);
    node.setAttribute('contenteditable', 'false');
    // node.setAttribute('title', 'Template Variable'); // Removed to prevent native tooltip and use custom one only
    if (typeof value === 'string') {
      node.textContent = value; // If we pass the text like [VarName] directly
    }
    return node;
  }

  static formats(domNode) {
    // We might want to return the text content or a specific attribute if needed
    return domNode.textContent; // Or a specific attribute from domNode
  }

  format(name, value) {
    if (name === TemplateVariableBlot.blotName && !value) {
      // Handle unformatting if necessary, though these should be static
      super.format(name, value);
    } else {
      super.format(name, value);
    }
  }
}

Quill.register(TemplateVariableBlot);

// --- Custom Quill Blot for AI Modified Spans ---
class AiModifiedBlot extends Inline {
  static create(value) {
    const node = super.create(value);
    node.setAttribute('class', 'ai-modified');
    return node;
  }

  static formats(domNode) {
    if (domNode.classList.contains('ai-modified')) {
      return true;
    }
    return super.formats(domNode);
  }
}
AiModifiedBlot.blotName = 'ai-modified';
AiModifiedBlot.tagName = 'span';
AiModifiedBlot.className = 'ai-modified';
Quill.register(AiModifiedBlot);

// pdfjs worker configuration removed
 
const GET_GENERATED_DOCUMENT = gql`
  query GetGeneratedDocument($id: ID!) {
    generatedDocument(id: $id) {
      id
      name
      htmlContent
    }
  }
`;

const DocumentDetailPage = () => {
  console.log('DocumentDetailPage rendering...'); 
  const { uuid } = useParams(); 
  console.log('Extracted UUID from params:', uuid); // Log UUID

  const [docxHtml, setDocxHtml] = useState('');
  // const [loadingDocx, setLoadingDocx] = useState(true); // Now managed by useQuery
  // const [docxError, setDocxError] = useState(null);   // Now managed by useQuery
  const [tooltipVisible, setTooltipVisible] = useState(false);
  const [tooltipContent, setTooltipContent] = useState('');
  const [tooltipPosition, setTooltipPosition] = useState({ top: 0, left: 0 });
  const [containerWidth, setContainerWidth] = useState(null); // Kept for layout consistency, may be removed if not used
  const quillRef = useRef(null);
  const tooltipAnchorRef = useRef(null); // Ref for the Grid item that anchors the tooltip

  const containerRef = useRef(null); // Kept for layout consistency
  const debounceTimeoutRef = useRef(null); // Related to containerWidth, kept for now

  useEffect(() => {
    const observer = new ResizeObserver(entries => {
      for (let entry of entries) {
        // Clear the previous timeout if resize happens again quickly
        if (debounceTimeoutRef.current) {
          clearTimeout(debounceTimeoutRef.current);
        }
        // Set a new timeout to update width after 150ms of inactivity
        debounceTimeoutRef.current = setTimeout(() => {
          const newWidth = Math.floor(entry.contentRect.width);
          console.log(`Debounced ResizeObserver: newWidth = ${newWidth}`); 
          setContainerWidth(newWidth); 
        }, 150); // Debounce delay of 150ms
      }
    });

    if (containerRef.current) {
      observer.observe(containerRef.current);
      const initialWidth = Math.floor(containerRef.current.offsetWidth);
      if(initialWidth !== containerWidth) {
        setContainerWidth(initialWidth);
      }
    }

    const node = containerRef.current; 
    return () => {
      if (node) { 
        observer.unobserve(node);
      }
      observer.disconnect(); 
      if (debounceTimeoutRef.current) { // Clear timeout on cleanup
        clearTimeout(debounceTimeoutRef.current);
      }
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps -- containerWidth is intentionally omitted, ResizeObserver handles updates
  }, []); // Run only once on mount, ResizeObserver handles subsequent updates

  // Apollo useQuery hook for fetching the document
  const { loading: loadingDocx, error: queryError, data: documentData } = useQuery(GET_GENERATED_DOCUMENT, {
    variables: { id: uuid },
    skip: !uuid, // Skip the query if uuid is not available
    fetchPolicy: 'network-only', // Ensures fresh data, adjust if caching is desired
  });

  // Effect for custom tooltip event listeners
  const ESTIMATED_TOOLTIP_HEIGHT = 35; // Approximate height of the tooltip in pixels
  const GAP_ABOVE_ELEMENT = 15;     // Gap between element and tooltip in pixels

  useEffect(() => {
    if (!quillRef.current || !docxHtml || !tooltipAnchorRef.current) {
      return;
    }

    const editor = quillRef.current.getEditor();
    const editorRoot = editor.root;

    const handleMouseOver = (event) => {
      const target = event.target.closest('.template-variable');
      // Ensure tooltipAnchorRef.current is available
      if (target && tooltipAnchorRef.current) { 
        const anchorRect = tooltipAnchorRef.current.getBoundingClientRect();
        const targetRect = target.getBoundingClientRect();

        // Calculate position for the tooltip to be above the target, relative to the anchor
        const top = targetRect.top - anchorRect.top - ESTIMATED_TOOLTIP_HEIGHT - GAP_ABOVE_ELEMENT;
        const left = targetRect.left - anchorRect.left + (targetRect.width / 2);

        setTooltipContent(target.innerText);
        setTooltipPosition({ top: top, left: left });
        setTooltipVisible(true);
      }
    };

    const handleMouseOut = (event) => {
      if (quillRef.current) {
        const editorRoot = quillRef.current.getEditor().root;
        if (!event.relatedTarget || !editorRoot.contains(event.relatedTarget) || !event.relatedTarget.closest('.template-variable')) {
          setTooltipVisible(false);
        }
      }
    };

    // handleMouseMove is no longer needed as tooltip is positioned relative to the element

    editorRoot.addEventListener('mouseover', handleMouseOver);
    editorRoot.addEventListener('mouseout', handleMouseOut);

    return () => {
      editorRoot.removeEventListener('mouseover', handleMouseOver);
      editorRoot.removeEventListener('mouseout', handleMouseOut);
    };
  }, [docxHtml, quillRef, tooltipAnchorRef, setTooltipContent, setTooltipPosition, setTooltipVisible]); // Added tooltipAnchorRef to dependencies as it's read in useEffect

  useEffect(() => {
    if (!uuid) {
      console.log('No UUID, clearing docxHtml.');
      setDocxHtml('');
      return;
    }

    if (loadingDocx) {
      console.log(`GraphQL query for document '${uuid}' is loading...`);
    } else if (queryError) {
      console.error(`Error fetching document '${uuid}' via GraphQL:`, queryError);
      setDocxHtml(''); 
    } else if (documentData && documentData.generatedDocument) {
      console.log(`Document '${uuid}' fetched successfully via GraphQL. HTML length: ${documentData.generatedDocument.htmlContent.length}`);
      setDocxHtml(documentData.generatedDocument.htmlContent);
    } else if (!loadingDocx && !documentData) {
      console.log(`No data returned for document '${uuid}' and not loading.`);
      setDocxHtml('');
    }
  }, [uuid, loadingDocx, queryError, documentData]);

  return (
    console.log(`Rendering document view for UUID: ${uuid}`),
    <Box sx={{ pb: 4, pt: 0 }}> 
      <Grid container spacing={3}>
        {/* AI Assistant Section - Now on the left */}
        <Grid item xs={12} md={3} sx={{ minHeight: 'calc(100vh - 150px)' /* Consider adjusting minHeight or removing position relative if not needed */ }}>
           <Box sx={{ mb: 1 }}> 
             <Typography
               variant="body2"
               color="text.secondary"
               component={Link}
               to="/documents"
               sx={{ textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
             >
               &larr; Back to Documents
             </Typography>
            
             <Typography
               variant="subtitle1"
               color="text.primary"
               sx={{ fontWeight: 'medium' }}
             >
               RFP: Ascendia
             </Typography>
           </Box>
          <AIAssistant />
        </Grid>

        {/* Document Editor Section - Now on the right */}
        <Grid item ref={tooltipAnchorRef} xs={12} md={9} sx={{ position: 'relative', display: 'flex', flexDirection: 'column' }}>
           <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', mb: 1, minHeight: '48px' /* Match TitleBlock content height */ }}>
             <Typography variant="caption" color="text.secondary" sx={{ mr: 2 }}>
               Last saved 2 days ago
             </Typography>
             <Button variant="contained" color="success" onClick={() => console.log('Submit for Review clicked (no-op)')}>
               Submit For Review
             </Button>
           </Box>
          <Paper elevation={2} sx={{ p: 2, display: 'flex', flexDirection: 'column', flexGrow: 1, overflow: 'hidden' }}>
           {/* DOCX Document Editor Container */}
             <Box
               ref={containerRef}
               sx={{
                 flexGrow: 1,
                 display: 'flex',
                 flexDirection: 'column',
                 minHeight: 0,
                 '.ql-toolbar': {
                   position: 'sticky',
                   top: 0,
                   backgroundColor: (theme) => theme.palette.background.paper,
                   zIndex: 1050,
                   borderTopLeftRadius: (theme) => theme.shape.borderRadius + 'px',
                   borderTopRightRadius: (theme) => theme.shape.borderRadius + 'px',
                   borderBottom: (theme) => `1px solid ${theme.palette.divider}`,
                 },
                 '.ql-container': {
                   flexGrow: 1,
                   overflowY: 'auto',
                   maxHeight: '700px',
                   borderBottomLeftRadius: (theme) => theme.shape.borderRadius + 'px',
                   borderBottomRightRadius: (theme) => theme.shape.borderRadius + 'px',
                   border: 'none !important',
                   fontSize: '1rem',
                 },
                 '.ql-editor': {
                   height: '100%',
                   padding: '16px',
                 },
                 '.template-variable': {
                   backgroundColor: '#E3F2FD',
                   borderLeft: '4px solid #1976D2',
                   padding: '1px 5px 1px 8px',
                   borderRadius: '2px',
                   cursor: 'default',
                   display: 'inline-block',
                   color: '#1C1C1C',
                 },
                 '.ai-modified': {
                   backgroundColor: '#E3F2FD',
                   borderLeft: '4px solid #1976D2',
                   padding: '1px 5px 1px 8px',
                   borderRadius: '2px',
                   cursor: 'default',
                   display: 'inline-block',
                   color: '#1C1C1C',
                 },
               }}
             >
               {loadingDocx && (
                 <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', flexGrow: 1, p: 3 }}>
                   <LinearProgress sx={{ width: '80%', mb: 2 }} />
                   <Typography>Loading document...</Typography>
                 </Box>
               )}
               {queryError && !loadingDocx && (
                 <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', flexGrow: 1, p: 3, color: 'error.main', textAlign: 'center' }}>
                   <Typography variant="body1">{queryError.message}</Typography>
                 </Box>
               )}
               {!uuid && !loadingDocx && !queryError && (
                 <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', flexGrow: 1, p: 3 }}>
                   <Typography>No document selected or UUID missing.</Typography>
                 </Box>
               )}
               {tooltipVisible && (
                 <Box
                   sx={{
                     position: 'absolute',
                     top: tooltipPosition.top,
                     left: tooltipPosition.left,
                     backgroundColor: 'rgba(0, 0, 0, 0.85)',
                     color: 'white',
                     padding: '5px 10px',
                     borderRadius: '4px',
                     fontSize: '0.8rem',
                     zIndex: 1300, // Ensure it's above other elements
                     pointerEvents: 'none', // So it doesn't interfere with mouse events on elements below it
                     transform: 'translateX(-50%)' // Center horizontally
                   }}
                 >
                   {tooltipContent}
                 </Box>
               )}
               {!loadingDocx && !queryError && uuid && docxHtml && (
                 <ReactQuill
                   ref={quillRef}
                   theme="snow"
                   value={docxHtml}
                   onChange={(content, delta, source, editor) => {
                     if (source === 'user') {
                       setDocxHtml(content);
                     }
                   }}
                   modules={{
                     toolbar: [
                       [{ 'header': [1, 2, 3, 4, 5, 6, false] }],
                       ['bold', 'italic', 'underline', 'strike'],
                       [{ 'color': [] }, { 'background': [] }],
                       [{ 'font': [] }],
                       [{ 'align': [] }],
                       [{ 'list': 'ordered'}, { 'list': 'bullet' }, { 'indent': '-1'}, { 'indent': '+1' }],
                       ['blockquote', 'code-block'],
                       ['link', 'image'],
                       ['clean']
                     ],
                   }}
                   style={{
                     display: 'flex',
                     flexDirection: 'column',
                     flexGrow: 1,
                     height: '100%',
                     overflow: 'hidden'
                   }}
                 />
               )}
             </Box>
           </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

export default DocumentDetailPage;
